/**
 * RecordingOverlay 组件
 * 录音时的全屏覆盖组件，类似微信录音界面
 * 使用 React Portal 渲染到 document.body，确保覆盖整个页面
 */
import React, { memo, useCallback, useRef, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, VStack, Text, Circle } from '@chakra-ui/react';
import { VoiceVisualizer } from 'react-voice-visualizer';

interface RecordingOverlayProps {
  isVisible: boolean;
  duration: number;
  gestureState?: 'normal' | 'dragging-cancel' | 'dragging-edit' | 'dragging-hold';
  onGestureStateChange?: (newState: 'normal' | 'dragging-cancel' | 'dragging-edit' | 'dragging-hold') => void;
  onCancel?: () => void;
  onEdit?: () => void;
  recorderControls?: any; // 录音控制器，用于波形显示
  // 暴露区域检测函数给父组件
  onRegisterPointerHandler?: (handler: (e: React.PointerEvent) => void) => void;
  // 🔥 新增：时长限制相关
  maxRecordingDuration?: number; // 最大录音时长（秒）
  warningThreshold?: number; // 提醒阈值（秒）
  // 🔥 新增：保持模式相关
  onHold?: () => void; // 进入保持模式回调
  onSend?: () => void; // 发送录音回调（保持模式下）
  isHoldMode?: boolean; // 是否处于保持模式
  // 🔥 新增：嵌入模式相关
  isEmbedded?: boolean; // 是否为嵌入模式，影响覆盖层渲染方式
}

const RecordingOverlayComponent: React.FC<RecordingOverlayProps> = ({
  isVisible,
  duration,
  gestureState = 'normal',
  onGestureStateChange,
  onCancel,
  onEdit,
  recorderControls,
  onRegisterPointerHandler,
  maxRecordingDuration = 15, // 🔥 默认15秒
  warningThreshold = 10, // 🔥 默认提前10秒提醒
  onHold, // 🔥 进入保持模式回调
  onSend, // 🔥 发送录音回调
  isHoldMode = false, // 🔥 是否处于保持模式
  isEmbedded = false // 🔥 是否为嵌入模式
}) => {
  const cancelButtonRef = useRef<HTMLDivElement>(null);
  const editButtonRef = useRef<HTMLDivElement>(null);
  const holdButtonRef = useRef<HTMLDivElement>(null); // 🔥 保持按钮ref

  // 检查点是否在元素区域内
  const isPointInElement = useCallback((x: number, y: number, element: HTMLElement | null): boolean => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }, []);

  // 处理指针移动事件
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!onGestureStateChange) return;

    const { clientX, clientY } = e;

    // 检查是否在取消按钮区域
    if (isPointInElement(clientX, clientY, cancelButtonRef.current)) {
      onGestureStateChange('dragging-cancel');
    }
    // 🔥 检查是否在保持按钮区域（只在非保持模式下）
    else if (!isHoldMode && isPointInElement(clientX, clientY, holdButtonRef.current)) {
      onGestureStateChange('dragging-hold');
    }
    // 检查是否在编辑按钮区域
    else if (isPointInElement(clientX, clientY, editButtonRef.current)) {
      onGestureStateChange('dragging-edit');
    }
    // 否则回到正常状态
    else {
      onGestureStateChange('normal');
    }

  }, [onGestureStateChange, isPointInElement, isHoldMode]);

  // 注册区域检测函数给父组件
  useEffect(() => {
    if (onRegisterPointerHandler) {
      onRegisterPointerHandler(handlePointerMove);
    }
  }, [onRegisterPointerHandler, handlePointerMove]);

  // 使用 useMemo 稳定波形组件，避免不必要的重新渲染
  // 必须在所有条件返回之前调用所有 hooks
  const waveformComponent = useMemo(() => {
    // 如果有传入的录音控制器，直接使用它，不要约束其能力
    if (recorderControls) {
      return (
        <VoiceVisualizer
          key="voice-visualizer" // 添加稳定的 key
          controls={recorderControls}
          width={400} // 更大的宽度，占满一行
          height={120} // 更大的高度
          backgroundColor="rgba(0, 0, 0, 0.7)" // 与覆盖层背景统一
          mainBarColor="#ffffff"
          secondaryBarColor="#cccccc"
          barWidth={2} // 更宽的波形条
          gap={1} // 更大的间距
          rounded={5}
          speed={3}
          isControlPanelShown={false}
          isDefaultUIShown={false}
          onlyRecording={true}
          animateCurrentPick={true}
          fullscreen={false}
        />
      );
    }

    // 如果没有录音控制器，显示简单的波形动画
    return (
      <Box
        key="fallback-animation" // 添加稳定的 key
        width="400px" // 与 VoiceVisualizer 保持一致
        height="120px" // 与 VoiceVisualizer 保持一致
        bg="rgba(0, 0, 0, 0.7)" // 与覆盖层背景统一
        borderRadius="12px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="1px solid rgba(255,255,255,0.2)"
      >
        <Text color="white" fontSize="sm">
          录音中...
        </Text>
      </Box>
    );
  }, [recorderControls]); // 只在 recorderControls 变化时重新创建

  // 不再使用全局事件监听器，改为在覆盖层上直接处理

  // 如果不可见，返回 null（在所有 hooks 调用之后）
  if (!isVisible) return null;

  // 格式化时长显示
  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${remainingSeconds}"`;
  };

  // 获取底部提示文字
  const getBottomHintText = () => {
    switch (gestureState) {
      case 'dragging-cancel':
        return '松开取消录音';
      case 'dragging-edit':
        return '松开转为文字编辑';
      case 'dragging-hold':
        return '松开进入保持模式';
      default:
        if (isHoldMode) {
          return '保持录音中 - 选择操作：取消 | 发送 | 编辑';
        }
        if (onHold && !isHoldMode) {
          return '松开发送 或 拖拽到📌保持录音';
        }
        return '松开发送';
    }
  };

  // 🔥 根据 isEmbedded 决定渲染目标
  const getPortalTarget = () => {
    if (isEmbedded) {
      // 嵌入模式：查找 ChatDetail 容器
      // 使用特定的 data 属性来精确定位 ChatDetail 容器
      const chatDetailContainer = document.querySelector('[data-chat-detail-container="true"]');
      return chatDetailContainer || document.body;
    } else {
      // 非嵌入模式：渲染到 document.body，覆盖整个页面
      return document.body;
    }
  };

  // 🔥 使用 Portal 将覆盖层渲染到合适的容器
  return createPortal(
    <>
      {/* CSS 动画定义 */}
      <style>
        {`
          @keyframes waveform-pulse {
            0% {
              transform: scaleY(0.4);
              opacity: 0.6;
            }
            50% {
              transform: scaleY(1.0);
              opacity: 0.9;
            }
            100% {
              transform: scaleY(0.6);
              opacity: 0.7;
            }
          }

          @keyframes recording-pulse {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0.7;
            }
            100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
          }

          @keyframes recording-glow {
            0% {
              box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
            }
            50% {
              box-shadow: 0 0 25px rgba(255, 255, 255, 0.7);
            }
            100% {
              box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
            }
          }
        `}
      </style>

      {/* 覆盖层 - 根据 isEmbedded 调整定位方式 */}
      <Box
        position={isEmbedded ? "absolute" : "fixed"} // 🔥 嵌入模式使用 absolute，非嵌入模式使用 fixed
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.7)"
        backdropFilter="blur(10px)"
        zIndex={9999}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          touchAction: 'none'
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          e.preventDefault();
        }}
      >
        {/* 主录音区域 */}
        <VStack spacing={8} align="center">
          {/* 波形显示区域 - 放在最上面，更大更突出 */}
          <Box
            position="relative"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={4}
            width="100%"
            maxWidth="400px"
          >
            {/* 波形动画 - 使用稳定的组件，避免重新渲染 */}
            {waveformComponent}

            {/* 录音时长 - 播放器风格显示 */}
            {(() => {
              const currentSeconds = duration / 1000;
              const remainingTime = maxRecordingDuration - currentSeconds;
              const shouldShowWarning = remainingTime <=  warningThreshold;

              return (
                <Text
                  fontSize="xl"
                  fontWeight="light"
                  color="white"
                  textAlign="center"
                  letterSpacing="wider"
                  opacity={0.9}
                >
                  {formatDuration(duration)}
                  {shouldShowWarning && remainingTime > 0 && (
                    <span
                      style={{
                        color: '#ff4444',
                        fontWeight: 'bold',
                        marginLeft: '12px',
                        textShadow: '0 0 8px rgba(255, 68, 68, 0.6)',
                        animation: 'pulse 1s infinite'
                      }}
                    >
                      -{Math.ceil(remainingTime)}s
                    </span>
                  )}
                  {remainingTime <= 0 && (
                    <span
                      style={{
                        color: '#ff2222',
                        fontWeight: 'bold',
                        marginLeft: '12px',
                        textShadow: '0 0 10px rgba(255, 34, 34, 0.8)',
                        animation: 'pulse 0.5s infinite'
                      }}
                    >
                      -TIME UP
                    </span>
                  )}
                </Text>
              );
            })()}
          </Box>

          {/* 操作提示 */}
          <Text
            fontSize="lg"
            color="white"
            textAlign="center"
            opacity={0.8}
            fontWeight="medium"
          >
            {getBottomHintText()}
          </Text>
        </VStack>

        {/* 底部操作区域 */}
        <Box
          position="absolute"
          bottom="80px"
          left="50%"
          transform="translateX(-50%)"
          display="flex"
          alignItems="center"
          gap={16}
        >
          {/* 取消按钮 */}
          <VStack spacing={2} align="center" ref={cancelButtonRef}>
            <Circle
              size="60px"
              bg={gestureState === 'dragging-cancel' ? 'red.500' : 'gray.600'}
              color="white"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ bg: 'red.500' }}
              onClick={onCancel}
            >
              <Text fontSize="2xl">✕</Text>
            </Circle>
            <Text fontSize="sm" color="white" opacity={0.8}>
              取消
            </Text>
          </VStack>

          {/* 🔥 中间按钮：保持/发送 */}
          {(onHold || onSend) && (
            <VStack spacing={2} align="center" ref={holdButtonRef}>
              <Circle
                size="60px"
                bg={
                  gestureState === 'dragging-hold' ? 'green.400' :
                  isHoldMode ? "green.500" : "green.600"
                }
                color="white"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ bg: 'green.500' }}
                onClick={() => {
                  if (isHoldMode && onSend) {
                    console.log('发送按钮被点击');
                    onSend();
                  } else if (!isHoldMode && onHold) {
                    console.log('保持按钮被点击');
                    onHold();
                  }
                }}
              >
                <Text fontSize="xl">{isHoldMode ? '✓' : '📌'}</Text>
              </Circle>
              <Text fontSize="sm" color="white" opacity={0.8}>
                {isHoldMode ? '发送' : '保持'}
              </Text>
            </VStack>
          )}

          {/* 转文字按钮 */}
          <VStack spacing={2} align="center" ref={editButtonRef}>
            <Circle
              size="60px"
              bg={gestureState === 'dragging-edit' ? 'blue.500' : 'gray.600'}
              color="white"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ bg: 'blue.500' }}
              onClick={onEdit}
            >
              <Text fontSize="xl">文</Text>
            </Circle>
            <Text fontSize="sm" color="white" opacity={0.8}>
              转文字 发送
            </Text>
          </VStack>
        </Box>
      </Box>
    </>,
    getPortalTarget() // 🔥 根据 isEmbedded 渲染到合适的容器
  );
};

export const RecordingOverlay = memo(RecordingOverlayComponent);
export default RecordingOverlay;
